<!-- Copyright (c) 2025 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="ack-main">
    <div class="left-menu" ref="leftMenu">
      <Menus :menu="menu" @menuClicked="menuClicked" />
    </div>
    <div class="right-container">
      <div class="top-section">
        <div class="filter-desc">
          {{ filterText }}
        </div>
        <ButtonFilter
          class="button-filter-component"
          filterPosition="right"
          buttonText="Change Filter"
          :filterWidth="300"
          :isOpen.sync="mainFilterIsOpen"
          :buttonIsDisabled="mainFilterIsDisabled"
        >
          <div class="filter-popup">
            <BoxContainer
              title="Date"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterListingByDate"
            >
              <div>
                <DateTimePicker
                  ref="fromdate"
                  placeholder="From"
                  :isRequired="filterListingByDate"
                  :showTime="false"
                  :isDisabled="!filterListingByDate"
                  :value.sync="ackFromDate"
                  :errorMessage.sync="fromDateErrMsg"
                />
                <DateTimePicker
                  ref="todate"
                  placeholder="To"
                  :isRequired="filterListingByDate"
                  :showTime="false"
                  :isDisabled="!filterListingByDate"
                  :value.sync="ackToDate"
                  :errorMessage.sync="toDateErrMsg"
                />
              </div>
            </BoxContainer>
            <BoxContainer
              title="Search"
              :isCheckBox="true"
              style="margin-bottom: 5px"
              :isChecked.sync="filterListingBySearch"
            >
              <div>
                <Dropdown
                  ref="searchby"
                  placeholder="Search By"
                  :items="searchFilterItems"
                  itemKey="Search By"
                  :isDisabled="!filterListingBySearch"
                  :isRequired="filterListingBySearch"
                  :selectedObject.sync="selectedSearchBy"
                  :errorMessage.sync="searchByErrMsg"
                />
                <Dropdown
                  ref="searchcriteria"
                  placeholder="Search Criteria"
                  :items="searchCriteriaItems"
                  itemKey="name"
                  :isDisabled="!filterListingBySearch"
                  :isRequired="filterListingBySearch"
                  :selectedObject.sync="selectedSearchCriteria"
                  :errorMessage.sync="searchCriteriaErr"
                />
                <Input
                  ref="search"
                  placeholder="Search"
                  :inputValue.sync="searchValue"
                  :isDisabled="!filterListingBySearch"
                  :isRequired="filterListingBySearch"
                  :errorMessage.sync="searchValueErr"
                />
              </div>
            </BoxContainer>
            <button @click="refreshClicked" :disabled="!filterListingByDate && !filterListingBySearch">Refresh</button>
          </div>
        </ButtonFilter>
        <div>Last Refreshed: {{ lastRefreshed }}</div>
      </div>
      <div class="bottom-section">
        <TableBox
          title="Acknowledgement Letter"
          :tableData="ackLetterListing"
          :dataType="ackLetterListingDataType"
          :selectedRow.sync="selectedACKListing"
          :loading="ackLetterTableBoxLoading"
          style="height: 100%"
        />
      </div>
    </div>

    <PopupEntry
      v-if="showPopup"
      :show.sync="showPopup"
      :title="popupTitle"
      :createdBy="createdBy"
      :updatedBy="updatedBy"
      @save="saveClicked"
      :requiredError="requiredError"
      @requiredClicked="requiredClicked"
      :successMessages="successMessages"
      :errorMessages="errorMessages"
      :viewMode="viewMode"
      :cancelButton="viewMode ? 'Close' : 'Cancel'"
      style="display: flex; flex-direction: row; justify-content: space-around; overflow: scroll"
      :canPrint="true"
      @print="popupPrintClicked"
    >
      <div class="ack-entries">
        <!-- Summary of Events -->
        <BoxContainer v-if="action != 'N'" title="Summary of Events" :isCollapsible="true">
          <div class="summary-row">
            <div class="summary-block" v-for="(m, i) in summaryItems" :key="i">
              <Tooltip v-if="m['visible']" class="milestone-tooltip" :width="210" :tooltipText="m['Label'].join(', ')">
                <div></div>
              </Tooltip>
              <div
                :id="m['Title']"
                v-if="m['Title'] == 'Indicator'"
                class="summary-item"
                :style="{ backgroundColor: m['bgcolorToShow'] }"
              >
                {{ m['indicatorToShow'] }}
              </div>
              <div v-if="m['Title'] == 'Print Count'" class="summary-item" style="min-width: 120px">
                {{ m['indicatorToShow'] }}
                <img :src="m['Src']" height="20px" />
              </div>
            </div>
          </div>
          <div class="summary-row">
            <div v-for="(bg, val, i) in newLegend" :key="i" class="legend-items">
              <div class="legend-box" :style="{ backgroundColor: `${bg}` }"></div>
              {{ val }}
            </div>
          </div>
        </BoxContainer>

        <div class="ack-header">
          <!-- Ack Letter Details -->
          <BoxContainer title="Ack. Letter Details" class="header-containers">
            <div style="margin-top: 5px">
              <Input
                ref="ackletterno"
                placeholder="Ack. Letter No."
                :inputValue.sync="ackLetterNo"
                :errorMessage.sync="ackLetterNoErr"
                :isDisabled="true"
                :isRequired="true"
                validator="text,25"
                style="margin-bottom: 5px"
              />
              <DateTimePicker
                ref="ackletterdate"
                placeholder="Ack. Letter Date"
                :value.sync="ackLetterDate"
                :errorMessage.sync="ackLetterDateErr"
                :showTime="false"
                :isRequired="true"
                :isDisabled="viewMode"
                :maxDate="new Date('2079-06-06')"
                class="input-spacing"
              />
              <InputQuery
                ref="custno"
                placeholder="Customer No."
                :items="customerList"
                itemKey="Code - Customer"
                :selectedObject.sync="selectedCustomer"
                :errorMessage.sync="customerErr"
                :isRequired="true"
                :isDisabled="viewMode || selectedDocuments.length > 0"
                @selected="customerSelected"
                validator="text,105"
                :tableBoxHideColumns="['Code - Customer']"
                class="input-spacing"
              />

              <Input
                ref="attn"
                placeholder="Attn."
                :inputValue.sync="attn"
                :errorMessage.sync="attnErr"
                :isDisabled="viewMode"
                validator="text,200"
                class="input-spacing"
                style="margin-top: 10px"
              />
              <RemarksBox
                title="Remarks"
                :text.sync="remarks"
                :isDisabled="viewMode"
                :maxLength="200"
                class="input-spacing"
              />
            </div>
          </BoxContainer>

          <!-- Documents Selection -->
          <BoxContainer title="Documents Selection" class="header-containers">
            <div style="display: flex; justify-content: space-between">
              <BoxContainer title="Document Type" style="margin-left: 5px; min-height: 45px">
                <div style="display: flex; flex-direction: row; flex-wrap: wrap">
                  <div v-for="doc in docTypes" :key="doc['code']" style="margin-right: 5px">
                    <CheckBoxV2
                      :title="doc['name']"
                      :isChecked.sync="doc['isChecked']"
                      :isDisabled="doc['isDisabled'] || viewMode"
                      @checkBoxClicked="docTypeSelected($event, doc)"
                    />
                  </div>
                </div>
              </BoxContainer>
              <BoxContainer title="Document Date" style="height: auto; margin-right: 10px; padding-bottom: 10px">
                <div style="display: flex; padding-right: 10px">
                  <DateTimePicker
                    ref="docdatefrom"
                    placeholder="From"
                    :value.sync="docDateFrom"
                    :errorMessage.sync="docDateFromErr"
                    :showTime="false"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    :maxDate="new Date('2079-06-06')"
                    style="margin-right: 30px"
                  />
                  <DateTimePicker
                    ref="docdateto"
                    placeholder="To"
                    :value.sync="docDateTo"
                    :errorMessage.sync="docDateToErr"
                    :showTime="false"
                    :isRequired="true"
                    :isDisabled="viewMode"
                    :maxDate="new Date('2079-06-06')"
                  />
                </div>
              </BoxContainer>
            </div>
            <div style="display: flex; justify-content: space-between">
              <div style="margin-top: 15px; margin-left: 12px">
                <CheckBoxV2
                  title="Hide Taken Documents"
                  :isChecked.sync="hideTakenDocsChecked"
                  :isDisabled="viewMode"
                  @checkBoxClicked="hideTakenDocSelected"
                />
              </div>
              <div style="margin-top: 15px; margin-right: 10px">
                <button @click="refreshDocsClicked" :disabled="viewMode || disableRefreshDocs"
                  >Refresh Document List</button
                >
              </div>
            </div>
            <div style="overflow: hidden">
              <TableBox
                v-if="showDocList"
                title="Documents"
                :tableData.sync="documentList"
                :selectedRow.sync="selectedDocuments"
                :loading="documentListLoading"
                :dataType="documentListDataType"
                :maxSelection="documentMaxSelection"
                :hideColumns="['Taken']"
              />
            </div>
          </BoxContainer>
        </div>
      </div>
    </PopupEntry>
  </div>
</template>

<script>
export default {
  components: {},
  computed: {
    documentMaxSelection: function () {
      return this.action == '' ? 0 : -1;
    },

    disableRefreshDocs: function () {
      return !this.selectedCustomer || !this.docDateFrom || !this.docDateTo;
    },
  },
  data: function () {
    return {
      menu: [
        {
          title: 'New Acknowledgement Letter',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-add-new-50.png',
          enabled: false,
          key: 'mnuNew',
        },
        {
          title: 'Edit Acknowledgement Letter',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-edit-64%20(1).png',
          enabled: false,
          key: 'mnuEdit',
        },
        {
          title: 'Delete Acknowledgement Letter',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-reduce-50.png',
          enabled: false,
          key: 'mnuDelete',
        },
        {
          title: 'View Acknowledgement Letter',
          icon: 'https://dsmlib.logistics.systems/Icons/icons8-view-64.png',
          enabled: false,
        },
        {
          title: 'Print Acknowledgement Letter',
          icon: 'https://dsmlib.logistics.systems/Icons/Print.png',
          enabled: true,
          key: 'mnuPrint',
        },
      ],
      action: '',

      mainFilterIsOpen: false,
      filterText: '',
      mainFilterIsDisabled: false,
      filterListingByDate: true,
      ackFromDate: new Date(),
      fromDateErrMsg: '',
      ackToDate: new Date(),
      toDateErrMsg: '',
      lastRefreshed: '',

      filterListingBySearch: false,
      searchFilterItems: [
        { 'Search By': 'Ack. Letter #', key: 'ACK' },
        { 'Search By': 'Invoice #', key: 'IN' },
        { 'Search By': 'Debit Note #', key: 'DN' },
        { 'Search By': 'Credit Note #', key: 'CN' },
      ],
      selectedSearchBy: null,
      searchByErrMsg: '',

      searchCriteriaItems: [{ name: 'EQUALS' }, { name: 'STARTS WITH' }, { name: 'CONTAINS' }, { name: 'ENDS WITH' }],
      selectedSearchCriteria: null,
      searchCriteriaErr: '',

      searchValue: '',
      searchValueErr: '',

      ackLetterTableBoxLoading: false,
      ackLetterListing: [],
      ackLetterListingDataType: {
        Date: 'date',
        'Date Created': 'datetime',
        'Date Updated': 'datetime',
      },
      selectedACKListing: [],
      currentTabIndex: 0,

      //#region Popup entry general props
      viewMode: false,
      showPopup: false,
      popupTitle: '',
      requiredError: [],
      errorMessages: [],
      successMessages: [],
      createdBy: '',
      updatedBy: '',
      //#endregion

      //#region dropdown/inputquery items
      customerList: [],
      //#endregion

      //#region Summary of Events
      summaryItems: [
        {
          Title: 'Indicator',
          Label: ['New', 'Cancelled'],
          Indicator: [
            { code: 'N', name: 'New Acknowledgement Letter', bg: '#79d279' },
            { code: 'C', name: 'Cancelled', bg: '#FFFFFF' },
          ],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
        {
          Title: 'Print Count',
          Src: 'https://dsmlib.logistics.systems/Icons/Print.png',
          Label: ['Print Count'],
          Indicator: [{ code: '0', name: 'Print Count' }],
          indicatorToShow: '',
          bgcolorToShow: '',
          visible: true,
        },
      ],
      newLegend: {
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Completed: '#79d279',
        // eslint-disable-next-line vue/no-unused-properties -- used in template
        Pending: '#ffff99',
      },
      //#endregion

      //#region Ack Letter Details
      ackLetterDetails: [],
      showDocList: true,
      oriDocList: [],
      documentList: [],
      documentListDataType: {
        Amount: 'currency',
        'F.Total Amount': 'currency',
        Rebate: 'currency',
      },
      documentListLoading: false,
      oriSelectedDocuments: [],
      selectedDocuments: [],
      ackLetterNo: '',
      ackLetterNoErr: '',
      ackLetterDate: new Date(),
      ackLetterDateErr: '',
      selectedCustomer: null,
      customerErr: '',
      docDateFrom: new Date(),
      docDateFromErr: '',
      docDateTo: new Date(),
      docDateToErr: '',
      attn: '',
      attnErr: '',
      remarks: '',
      docTypes: [
        { name: 'Invoice', code: 'IN', isChecked: true, isDisabled: false },
        { name: 'Debit Note', code: 'DN', isChecked: true, isDisabled: false },
        { name: 'Credit Note', code: 'CN', isChecked: true, isDisabled: false },
      ],
      hideTakenDocsChecked: true,
      //#endregion
    };
  },
  props: {},
  methods: {
    menuClicked: async function (e) {
      this.clearAllData();
      this.clearAllMessages();
      this.mainFilterIsOpen = false;
      this.currentTabIndex = 0;
      switch (e['title']) {
        case 'New Acknowledgement Letter': {
          this.action = 'N';
          this.showPopup = true;
          this.viewMode = false;
          this.ackLetterNo = '**********';
          this.ackLetterDate = new Date();
          this.docDateFrom = new Date();
          this.docDateTo = new Date();
          this.popupTitle = e['title'];
          break;
        }

        case 'Edit Acknowledgement Letter': {
          if (this.selectedACKListing.length == 0) {
            this.$showAlert('error', 'Edit Acknowledgement Letter', 'Please select an acknowledgement letter to edit');
            return;
          } else if (this.selectedACKListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Acknowledgement Letter Already Cancelled',
              'You are not allowed to edit cancelled acknowledgement letter!',
            );
            return;
          }
          this.action = 'E';
          this.viewMode = false;
          await this.getAckLetter(e['title']);
          this.showPopup = true;
          this.$nextTick(() => {
            // this.loadSummaryOfEvents();
            this.updateSummaryOfEvents();
          });
          break;
        }

        case 'Delete Acknowledgement Letter': {
          if (this.selectedACKListing.length == 0) {
            this.$showAlert(
              'error',
              'Delete Acknowledgement Letter',
              'Please select an acknowledgement letter to delete',
            );
            return;
          } else if (this.selectedACKListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Acknowledgement Letter Already Cancelled',
              'You are not allowed to delete cancelled acknowledgement letter!',
            );
            return;
          }
          const confirmDelete = await this.$showAlert(
            'warning',
            'Delete Acknowledgement Letter',
            `Are you sure you want to delete acknowledgement letter ${this.selectedACKListing[0]['Ack. Letter No']}?`,
            [{ text: 'Yes', color: 'red', textColor: 'white' }, { text: 'No' }],
          );

          if (confirmDelete == 'No') return;

          this.$fullscreenLoading('Deleting Acknowledgement Letter');
          const results = await this.$apiCallV2('/AcknowledgementLetter/SaveAckLetter', {
            action: 'D',
            ackno: this.selectedACKListing[0]['Ack. Letter No'],
          });
          this.$fullscreenLoading();

          if (results[0]['Results'] == 0) {
            this.$showAlert(
              'success',
              'Deleted!',
              `Acknowledgement Letter ${this.selectedACKListing[0]['Ack. Letter No']} was successfully deleted.`,
            );
          } else {
            this.$showAlert('error', 'Delete Unsuccessful', `${results[0]['ErrMsg']}`);
          }
          this.ackLetterListing = [];
          this.selectedACKListing = [];
          this.$nextTick(async () => await this.refreshClicked());
          break;
        }

        case 'View Acknowledgement Letter': {
          if (this.selectedACKListing.length == 0) {
            this.$showAlert('error', 'View Acknowledgement Letter', 'Please select an acknowledgement letter to view');
            return;
          }
          this.action = '';
          this.viewMode = true;
          await this.getAckLetter(e['title']);
          this.showPopup = true;
          this.$nextTick(() => {
            // this.loadSummaryOfEvents();
            this.updateSummaryOfEvents();
          });
          break;
        }

        case 'Print Acknowledgement Letter':
          if (this.selectedACKListing.length == 0) {
            this.openPrintAckLetter();
          } else if (this.selectedACKListing[0]['Indicator'] == 'C') {
            this.$showAlert(
              'error',
              'Selected Acknowledgement Letter Already Cancelled',
              'You are not allowed to print cancelled acknowledgement letter!',
            );
          } else {
            this.openPrintAckLetter(this.selectedACKListing[0]['Ack. Letter No']);
          }
          break;
      }
    },

    refreshClicked: async function () {
      this.fromDateErrMsg = '';
      this.toDateErrMsg = '';
      this.searchByErrMsg = '';
      this.searchCriteriaErr = '';
      this.searchValueErr = '';
      let listingErr = false;

      if (this.filterListingByDate) {
        if (!this.ackFromDate) {
          this.fromDateErrMsg = 'Enter from date!';
          listingErr = true;
        }

        if (!this.ackToDate) {
          this.toDateErrMsg = 'Enter to date!';
          listingErr = true;
        }
      }

      if (this.filterListingBySearch) {
        if (!this.selectedSearchBy) {
          this.searchByErr = 'Select search by!';
          listingErr = true;
        }

        if (!this.selectedSearchCriteria) {
          this.searchCriteriaErr = 'Select search criteria!';
          listingErr = true;
        }

        if (!this.searchValue.trim()) {
          this.searchValueErr = 'Enter search value!';
          listingErr = true;
        }
      }

      if (listingErr) return;

      this.mainFilterIsOpen = false;
      const dataToFilter = {
        startdate: this.filterListingByDate ? this.ackFromDate.formatString('yyyy-MM-dd') : '',
        enddate: this.filterListingByDate ? this.ackToDate.formatString('yyyy-MM-dd') : '',
        searchfield: this.filterListingBySearch ? this.selectedSearchBy['key'] : '',
        searchcriteria: this.filterListingBySearch ? this.selectedSearchCriteria['name'] : '',
        searchvalue: this.filterListingBySearch ? this.searchValue : '',
      };

      const filterItems = [];
      if (this.filterListingByDate) {
        filterItems.push(
          `date from ${this.ackFromDate.formatString('yyyy-MM-dd')} to ${this.ackToDate.formatString('yyyy-MM-dd')}`,
        );
      }
      if (this.filterListingBySearch) {
        filterItems.push(
          `${this.selectedSearchBy['Search By']} ${this.selectedSearchCriteria['name']} ${this.searchValue}`,
        );
      }
      this.filterText = `Filter by ${filterItems.join(', ')}`;

      this.$fullscreenLoading('Getting Acknowledgement Letter Listing');
      this.ackLetterTableBoxLoading = true;
      this.ackLetterListing = [];
      this.ackLetterListing = await this.$apiCallV2('/AcknowledgementLetter/GetAckLetterListing', dataToFilter);
      this.lastRefreshed = new Date().formatString('yyyy-MM-dd HH:mm');
      this.ackLetterTableBoxLoading = false;
      this.$fullscreenLoading();
    },

    requiredClicked: function (item) {
      this.$nextTick(() => {
        this.$refs[item].setFocus();
      });
    },

    clearAllData: function () {
      this.createdBy = '';
      this.updatedBy = '';
      this.ackLetterNo = '';
      this.ackLetterDate = new Date();
      this.selectedCustomer = null;
      this.docDateFrom = new Date();
      this.docDateTo = new Date();
      this.selectedDocuments = [];
      this.attn = '';
      this.remarks = '';
      this.docTypes.forEach((doc) => {
        doc.isChecked = true;
        doc.isDisabled = false;
      });
      this.hideTakenDocsChecked = true;
      this.oriDocList = [];
      this.documentList = [];

      this.clearAllMessages();
    },

    clearAllMessages: function () {
      this.requiredError = [];
      this.successMessages = [];
      this.errorMessages = [];

      this.ackLetterNoErr = '';
      this.ackLetterDateErr = '';
      this.customerErr = '';
      this.docDateFromErr = '';
      this.docDateToErr = '';
      this.attnErr = '';
    },

    getAckLetter: async function (title) {
      this.popupTitle = title;
      if (this.selectedACKListing.length > 0) {
        const ackNo = this.selectedACKListing[0]['Ack. Letter No'];
        await this.populateAckLetterDetails(ackNo);
      }
    },

    updateSummaryOfEvents: function () {
      for (const item of this.summaryItems) {
        const title = item['Title'];
        const value = this.ackLetterDetails[title];

        if (title == 'Print Count') {
          item['indicatorToShow'] = value || '0';
        } else if (value && item['Indicator']) {
          const indicator = item['Indicator'].find((i) => i['code'] === value);
          if (indicator) {
            item['indicatorToShow'] = indicator['name'];
            item['bgcolorToShow'] = indicator['bg'];
            item['visible'] = true;
          }
        }
      }
    },

    populateAckLetterDetails: async function (ackNo) {
      this.$fullscreenLoading('Getting data');
      this.ackLetterDetails = [];
      this.ackLetterDetails = (
        await this.$apiCallV2('/AcknowledgementLetter/GetAckLetterDetails', {
          ackno: ackNo,
        })
      )['AckLetter']['AckHeader'];

      this.createdBy =
        this.ackLetterDetails['Created By'] ?
          `${this.ackLetterDetails['Created By']} ${new Date(this.ackLetterDetails['Date Created']).formatString('yyyy-MM-dd HH:mm')}`
        : '';
      this.updatedBy =
        this.ackLetterDetails['Updated By'] ?
          `${this.ackLetterDetails['Updated By']} ${new Date(this.ackLetterDetails['Date Updated']).formatString('yyyy-MM-dd HH:mm')}`
        : '';
      this.ackLetterNo = this.ackLetterDetails['RefNo'] || '';
      this.ackLetterDate = this.ackLetterDetails['Date'] ? new Date(this.ackLetterDetails['Date']) : null;
      this.selectedCustomer =
        this.ackLetterDetails['CustNo'] ?
          this.customerList.find((cust) => cust['Customer Code'] == this.ackLetterDetails['CustNo'])
        : null;
      this.docDateFrom = this.ackLetterDetails['StartDate'] ? new Date(this.ackLetterDetails['StartDate']) : null;
      this.docDateTo = this.ackLetterDetails['EndDate'] ? new Date(this.ackLetterDetails['EndDate']) : null;
      this.attn = this.ackLetterDetails['Attn'] || '';
      this.remarks = this.ackLetterDetails['Remarks'] || '';
      const counts = {
        IN: this.ackLetterDetails['CountIN'] || 0,
        DN: this.ackLetterDetails['CountDN'] || 0,
        CN: this.ackLetterDetails['CountCN'] || 0,
      };

      this.docTypes.forEach((doc) => {
        const count = counts[doc.code];
        if (count !== undefined) {
          doc.isChecked = count > 0;
          doc.isDisabled = count > 0;
        }
      });

      this.oriSelectedDocuments = this.ackLetterDetails['Documents'] ?? [];

      this.oriDocList = await this.getDocumentListing(
        this.selectedCustomer['Customer Code'] ?? '',
        this.docDateFrom ? this.docDateFrom.formatString('yyyy-MM-dd') : '',
        this.docDateTo ? this.docDateTo.formatString('yyyy-MM-dd') : '',
        this.action == 'N' ? '' : this.ackLetterNo,
      );

      const tempSelected = this.oriDocList.filter((doc) =>
        this.oriSelectedDocuments.some((selected) => {
          return doc['Doc No'] === selected['Doc No'] && doc['Doc Type'] === selected['Doc Type'];
        }),
      );

      const takenDocSelected = tempSelected.some((doc) => doc['Taken'] == '1');
      this.hideTakenDocsChecked = !takenDocSelected;

      this.filterDocumentList(this.oriSelectedDocuments);

      this.$fullscreenLoading();
    },

    getDocumentListing: async function (billcu, startdate, enddate, ackno) {
      const docList = await this.$apiCallV2('/AcknowledgementLetter/GetAckLetterDocumentsListing', {
        billcu: billcu,
        startdate: startdate,
        enddate: enddate,
        ackno: ackno,
      });
      return docList;
    },

    filterDocumentList: function (selectedDocs) {
      this.showDocList = false;
      let temp = [];
      this.documentListLoading = true;
      this.documentList = [];
      const selectedDocType = this.docTypes.filter((doc) => doc.isChecked).map((doc) => doc.code);

      temp = this.oriDocList.filter((doc) => selectedDocType.includes(doc['Doc Type']));

      if (this.hideTakenDocsChecked) {
        temp = temp.filter((doc) => doc['Taken'] != '1');
      }

      this.$nextTick(() => {
        this.documentList = temp;
        this.documentListLoading = false;

        if (selectedDocs.length == 0) {
          this.showDocList = true;
          return;
        }

        this.selectedDocuments = [];
        const docs = selectedDocs.map((d) => {
          return {
            'Doc No': d['Doc No'],
            'Doc Type': d['Doc Type'],
          };
        });

        const tempSelected = temp.filter((doc) =>
          docs.some((selected) => {
            return doc['Doc No'] === selected['Doc No'] && doc['Doc Type'] === selected['Doc Type'];
          }),
        );

        if (this.viewMode) {
          //if in view mode then just show selected records only 
          this.documentList = [];
          this.documentList = tempSelected;
        }
        this.$nextTick(() => {
          this.selectedDocuments = [...tempSelected];
          this.showDocList = true;
        });
      });
    },

    checkInputFields: function () {
      let error = false;

      if (!this.selectedCustomer) {
        this.customerErr = 'Customer is required!';
        this.requiredError.push({ key: 'custno', value: this.customerErr });
        error = true;
      }

      if (!this.ackLetterDate) {
        this.ackLetterDateErr = 'Ack. Letter Date is required!';
        this.requiredError.push({ key: 'ackletterdate', value: this.ackLetterDateErr });
        error = true;
      } else if (this.ackLetterDate && new Date(this.ackLetterDate) > new Date('2079-06-06')) {
        this.ackLetterDateErr = 'Ack. Letter Date Invalid!';
        this.requiredError.push({ key: 'ackletterdate', value: this.ackLetterDateErr });
        error = true;
      } else if (this.ackLetterDate && new Date(this.ackLetterDate) < new Date('1900-01-01')) {
        this.ackLetterDateErr = 'Ack. Letter Date Invalid!';
        this.requiredError.push({ key: 'ackletterdate', value: this.ackLetterDateErr });
        error = true;
      }

      if (!this.docDateFrom) {
        this.docDateFromErr = 'From Date is required!';
        this.requiredError.push({ key: 'docdatefrom', value: this.docDateFromErr });
        error = true;
      } else if (this.docDateFrom && new Date(this.docDateFrom) > new Date('2079-06-06')) {
        this.docDateFromErr = 'From Date Invalid!';
        this.requiredError.push({ key: 'docdatefrom', value: this.docDateFromErr });
        error = true;
      } else if (this.docDateFrom && new Date(this.docDateFrom) < new Date('1900-01-01')) {
        this.docDateFromErr = 'From Date Invalid!';
        this.requiredError.push({ key: 'docdatefrom', value: this.docDateFromErr });
        error = true;
      } else if (this.docDateTo < this.docDateFrom) {
        this.docDateFromErr = 'From Date cannot be greater than To Date';
        this.requiredError.push({ key: 'docdatefrom', value: this.docDateFromErr });
        error = true;
      }

      if (!this.docDateTo) {
        this.docDateToErr = 'To Date is required!';
        this.requiredError.push({ key: 'docdateto', value: this.docDateToErr });
        error = true;
      } else if (this.docDateTo && new Date(this.docDateTo) > new Date('2079-06-06')) {
        this.docDateToErr = 'To Date Invalid!';
        this.requiredError.push({ key: 'docdateto', value: this.docDateToErr });
        error = true;
      } else if (this.docDateTo && new Date(this.docDateTo) < new Date('1900-01-01')) {
        this.docDateToErr = 'To Date Invalid!';
        this.requiredError.push({ key: 'docdateto', value: this.docDateToErr });
        error = true;
      }

      return error;
    },

    saveClicked: async function () {
      this.errorMessages = [];
      this.requiredError = [];
      this.clearAllMessages();

      if (this.checkInputFields()) return;
      if (this.selectedDocuments.length == 0) {
        this.$showAlert(
          'error',
          'No Documents Selected',
          'Please select at least one Invoice/Debit Note/Credit Note to proceed.',
        );
        return;
      }

      // Check if any documents are removed (only applicable for edit)
      let removedDocs = this.oriSelectedDocuments.filter(
        (doc) => !this.selectedDocuments.some((selected) => selected['Doc No'] === doc['Doc No']),
      );
      removedDocs = removedDocs.map((doc) => {
        return {
          ...doc,
          action: 'D',
        };
      });

      // Get new selected documents
      const highestSeq =
        this.oriSelectedDocuments.length == 0 ? 0 : Math.max(...this.oriSelectedDocuments.map((doc) => doc['seqNo']));

      let newAddedDocs = this.selectedDocuments.filter(
        (doc) => !this.oriSelectedDocuments.some((selected) => selected['Doc No'] === doc['Doc No']),
      );
      newAddedDocs = newAddedDocs.map((doc, index) => {
        return {
          ...doc,
          action: 'N',
          seqno: highestSeq + index + 1,
        };
      });

      // Get documents that are not removed or added
      // Have to filter oriSelectedDocuments instead of selectedDocuments to get the original seqNo
      let existingDocs = this.oriSelectedDocuments.filter(
        (doc) =>
          this.selectedDocuments.some((selected) => selected['Doc No'] === doc['Doc No']) &&
          !removedDocs.some((removed) => removed['Doc No'] === doc['Doc No']),
      );
      existingDocs = existingDocs.map((doc) => {
        return {
          ...doc,
          action: 'E',
          seqno: doc['seqNo'] || '1',
        };
      });

      let allDocs = removedDocs.concat(newAddedDocs).concat(existingDocs);
      allDocs = allDocs.map((doc) => {
        return {
          action: doc['action'],
          docno: doc['Doc No'],
          doctype: doc['Doc Type'],
          seqno: doc['seqno'] || '1',
        };
      });

      const dataToSave = {
        action: this.action,
        ackno: this.action == 'N' ? '' : this.ackLetterNo,
        ackdate: this.ackLetterDate.formatString('yyyy-MM-dd') ?? new Date().formatString('yyyy-MM-dd'),
        billcu: this.selectedCustomer['Customer Code'] ?? '',
        startdate: this.docDateFrom.formatString('yyyy-MM-dd') ?? new Date().formatString('yyyy-MM-dd'),
        enddate: this.docDateTo.formatString('yyyy-MM-dd') ?? new Date().formatString('yyyy-MM-dd'),
        attn: this.attn || '',
        remarks: this.remarks || '',
        documents: allDocs,
      };

      this.$fullscreenLoading('Saving Acknowledgement Letter');
      const saveResults = await this.$apiCallV2('/AcknowledgementLetter/SaveAckLetter', dataToSave);
      this.$fullscreenLoading();

      if (saveResults[0]['Results'] != 0) {
        if (this.action == 'N') {
          this.errorMessages.push(`There was an error creating Acknowledgement Letter: ${saveResults[0]['ErrMsg']}`);
        } else {
          this.errorMessages.push(`There was an error editing Acknowledgement Letter: ${saveResults[0]['ErrMsg']}`);
        }
        return;
      }

      this.clearAllData();
      const ackno = saveResults[0]['AckNo'];
      this.$nextTick(async () => {
        await this.populateAckLetterDetails(ackno);
        // this.loadSummaryOfEvents();
        this.updateSummaryOfEvents();
        await this.refreshClicked();
      });

      this.successMessages.push(`${saveResults[0]['ErrMsg']}`);
      this.viewMode = true;
      this.popupTitle = 'View Acknowledgement Letter';
      this.action = '';
    },

    refreshDocsClicked: async function () {
      let error = false;
      this.docDateFromErr = '';
      this.docDateToErr = '';

      if (!this.docDateFrom) {
        this.docDateFromErr = 'From Date is required!';
        error = true;
      } else if (this.docDateFrom && new Date(this.docDateFrom) > new Date('2079-06-06')) {
        this.docDateFromErr = 'From Date Invalid!';
        error = true;
      } else if (this.docDateFrom && new Date(this.docDateFrom) < new Date('1900-01-01')) {
        this.docDateFromErr = 'From Date Invalid!';
        error = true;
      } else if (this.docDateTo < this.docDateFrom) {
        this.docDateFromErr = 'From Date cannot be greater than To Date';
        error = true;
      }

      if (!this.docDateTo) {
        this.docDateToErr = 'To Date is required!';
        error = true;
      } else if (this.docDateTo && new Date(this.docDateTo) > new Date('2079-06-06')) {
        this.docDateToErr = 'To Date Invalid!';
        error = true;
      } else if (this.docDateTo && new Date(this.docDateTo) < new Date('1900-01-01')) {
        this.docDateToErr = 'To Date Invalid!';
        error = true;
      }

      if (error) return;
      if (this.selectedDocuments.length > 0) {
        const selectedDates = this.selectedDocuments.map((doc) => new Date(doc['Date']));
        const minDate = new Date(Math.min(...selectedDates));
        const maxDate = new Date(Math.max(...selectedDates));

        // Check if the selected from date is greater than the smallest selected document date or
        // if the selected to date is smaller than the greatest selected document date
        if (this.docDateFrom > minDate || this.docDateTo < maxDate) {
          const confirm = await this.$showAlert(
            'warning',
            'Refresh Document List Will Undo The Changes',
            'Are you sure you want to refresh the document list?',
            [{ text: 'Yes', color: 'red', textColor: 'white' }, { text: 'No' }],
          );
          if (confirm == 'No') return;
        }
      }

      this.$fullscreenLoading('Getting Documents');
      this.oriDocList = await this.getDocumentListing(
        this.selectedCustomer['Customer Code'] ?? '',
        this.docDateFrom ? this.docDateFrom.formatString('yyyy-MM-dd') : '',
        this.docDateTo ? this.docDateTo.formatString('yyyy-MM-dd') : '',
        this.action == 'N' ? '' : this.ackLetterNo,
      );
      this.filterDocumentList(this.selectedDocuments);
      this.$fullscreenLoading();
    },

    openPrintAckLetter: function (ackno) {
      let printAckRoute;
      if (ackno) {
        printAckRoute = this.$router.resolve(
          `/Home/${this.$route.params.user}/FINAR/Print Acknowledgement Letter?ackno=${ackno.trim?.() ?? ''}`,
        );
      } else {
        printAckRoute = this.$router.resolve(`/Home/${this.$route.params.user}/FINAR/Print Acknowledgement Letter`);
      }
      window.open(printAckRoute.href, '_blank');
    },

    popupPrintClicked: function () {
      if (this.ackLetterDetails['Indicator'] == 'C') {
        this.$showAlert(
          'error',
          `Acknowledgement Letter ${this.ackLetterNo} Already Cancelled`,
          'You are not allowed to print cancelled acknowledgement letter!',
        );
        return;
      }
      this.openPrintAckLetter(this.ackLetterNo);
    },

    customerSelected: function (newCust) {
      this.attn = newCust ? newCust['Customer Contact'] : '';
    },

    docTypeSelected: async function (selected, code) {
      if (this.selectedDocuments.length > 0) {
        const selectedType = Array.from(new Set(this.selectedDocuments.map((doc) => doc['Doc Type'])));
        if (!selected && selectedType.includes(code['code'])) {
          const confirm = await this.$showAlert(
            'warning',
            `${code['name']} Already Selected`,
            `Selected ${code['name']} will be removed. Proceed?`,
            [{ text: 'Yes', color: 'red', textColor: 'white' }, { text: 'No' }],
          );
          if (confirm == 'No') {
            code.isChecked = true;
            return;
          }
        }
      }
      this.filterDocumentList(this.selectedDocuments);
    },

    hideTakenDocSelected: function () {
      this.filterDocumentList(this.selectedDocuments);
    },
  },
  async mounted() {
    let sas = [];
    this.$bus.$on('pageSASControlAccess', (x) => {
      if (x.page == 'Acknowledgement Letter') {
        sas = x['data'];
      }
    });

    const mountedPromises = [];
    let mountedPromisesFulFilledCount = 0;

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'BILLCUST' }).then((result) => {
        this.customerList = result.map((c) => {
          return {
            ...c,
            'Code - Customer': `${c['Customer Code']} - ${c['Customer Name']}`,
          };
        });
        this.$topLoading(++mountedPromisesFulFilledCount / mountedPromises.length);
      }),
    );

    await Promise.allSettled(mountedPromises).then(async (_promiseOutcomes) => {
      await this.refreshClicked();

      this.menu = this.menu.map((menu) => ({
        ...menu,
        enabled: true,
      }));

      sas.forEach((k) => {
        const selectedMenu = this.menu.find((m) => m['key'] == k['Control']);
        if (selectedMenu) {
          selectedMenu[k['Properties'].toLowerCase()] = k['PropertiesValue'] != 'False';
        }
      });
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';
.ack-main {
  display: flex;
  font-size: 12px;
  padding: 10px 45px;

  > .left-menu {
    display: flex;
    justify-content: center;
  }

  > .right-container {
    width: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    > .top-section,
    > .bottom-section {
      background-color: white;
      border-radius: 5px;
      padding: 5px;
    }

    > .top-section {
      display: flex;
      align-items: center;
      justify-content: flex-end;

      > .filter-desc {
        padding-right: 8px;
      }
    }

    > .bottom-section {
      height: calc(100% - 38px - 5px);
      margin-top: 5px;
    }
  }
}

.button-filter-component {
  margin-right: 5px;
  position: relative;

  .filter-popup {
    display: flex;
    flex-direction: column;
    z-index: 10;

    > button {
      margin-top: 5px;
      width: fit-content;
      align-self: flex-end;
    }
  }
}

.ack-entries {
  height: calc(100% - 27px);
  width: 100%;
  overflow: scroll;

  .ack-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    height: 100%;
    width: 100%;

    > .header-containers {
      width: 50%;
      margin-right: 5px;
      padding-left: 5px;
      padding-right: 5px;

      .input-spacing {
        margin-top: 5px;
        margin-bottom: 5px;
      }
    }
  }
}

.summary-row {
  display: flex;
  flex-direction: row;
  width: fit-content;

  > :nth-child(1) {
    margin-right: 5px;
  }
}

.summary-block {
  position: relative;
  margin-bottom: 2px;
  margin-right: 3px;

  > .summary-item {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 210px;
    min-width: 210px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }
  > .summary-item-printer {
    border: 1px solid rgba(128, 128, 128, 0.5);
    max-width: 100px;
    -webkit-line-clamp: 2;
    display: -webkit-box;
    text-overflow: ellipsis;
    overflow: hidden;
    -webkit-box-orient: vertical;
    cursor: default;
    padding: 3px;
  }

  > .milestone-tooltip {
    position: absolute;
    text-overflow: wrap;
    height: 100%;
    width: 100%;
    top: 0;
    left: 0;
  }
}

.legend-items {
  display: flex;
  align-items: center;
  margin-right: 3px;

  > .legend-box {
    height: 10px;
    width: 10px;
    border: 1px solid rgba(128, 128, 128, 0.5);
    margin-right: 3px;
  }
}
</style>
